/**
 * Appends the current project_id as a query parameter to every REST SDK
 * request. The IsPartOfProject backend permission resolves the project from
 * this parameter.
 *
 * Already wired into `sspProjectConfig` (setup/openApiConfig) — consume that
 * config rather than building a new one.
 */
export const projectIdMiddleware = {
  pre: async (context) => {
    const { useMissionStore } = await import("@/store/modules/mission");
    const projectId = useMissionStore().currentMissionUuid;
    if (projectId && !/[?&]project_id=/.test(context.url)) {
      const separator = context.url.includes("?") ? "&" : "?";
      context.url += `${separator}project_id=${projectId}`;
    }
    return { url: context.url, init: context.init };
  },
};

const UUID_PATTERN = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;
const MAX_BODY_LENGTH = 500;

export const routeOf = (url) =>
  String(url)
    .split("?")[0]
    .replace(/^[a-z]+:\/\/[^/]+/i, "")
    .replace(UUID_PATTERN, ":uuid");

const readServerErrorBody = async (response) => {
  if (response.status < 500) return null;
  try {
    return (await response.text()).slice(0, MAX_BODY_LENGTH);
  } catch {
    return null;
  }
};

export class RestApiError extends Error {
  constructor({ method, url, status, body, response, cause }) {
    const route = routeOf(url);
    super(`${method} ${route} → ${status ?? "network error"}`, { cause });
    this.name = "RestApiError";
    this.method = method;
    this.url = url;
    this.route = route;
    this.status = status ?? null;
    this.body = body ?? null;
    this.response = response;
  }
}

const methodOf = (init) => (init?.method ?? "GET").toUpperCase();

export const restApiErrorMiddleware = {
  post: async ({ url, init, response }) => {
    if (response.ok) return response;
    throw new RestApiError({
      method: methodOf(init),
      url,
      status: response.status,
      body: await readServerErrorBody(response.clone()),
      response,
    });
  },
  onError: async ({ url, init, error }) => {
    if (error?.name === "AbortError") throw error;
    throw new RestApiError({
      method: methodOf(init),
      url,
      cause: error,
    });
  },
};
